import { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
} from "@mui/material";
import { CloudUpload as UploadIcon } from "@mui/icons-material";
import { useBulkCreateSchedules } from "../../hooks/price-change-schedules";
import { useMessage } from "../../sdk/hooks";
import { IBulkCreateResult } from "../../sdk/utils/api/price-change-schedule";

interface Props {
    open: boolean;
    onClose: () => void;
}

export function BulkUploadDialog({ open, onClose }: Props) {
    const { showError, showSuccess } = useMessage();
    const bulkCreate = useBulkCreateSchedules("priceChangeSchedules");

    const [startsAt, setStartsAt] = useState("");
    const [endsAt, setEndsAt] = useState("");
    const [platform, setPlatform] = useState(0);
    const [notes, setNotes] = useState("");
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [parsedRows, setParsedRows] = useState<{ variantId: string; sellingPrice: number }[]>([]);
    const [result, setResult] = useState<IBulkCreateResult | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setCsvFile(file);
        setResult(null);

        const reader = new FileReader();
        reader.onload = (event) => {
            const text = event.target?.result as string;
            const lines = text.trim().split("\n");
            if (lines.length < 2) return;

            const headerLine = lines[0].toLowerCase();
            const headers = headerLine.split(",").map((h) => h.trim());
            const variantIdIdx = headers.findIndex((h) => h === "variantid");
            const priceIdx = headers.findIndex((h) => h === "sellingprice" || h === "price");

            if (variantIdIdx === -1 || priceIdx === -1) {
                showError("CSV must have 'variantId' and 'sellingPrice' columns");
                return;
            }

            const rows = lines.slice(1).map((line) => {
                const cols = line.split(",").map((c) => c.trim());
                return {
                    variantId: cols[variantIdIdx] || "",
                    sellingPrice: Math.round(parseFloat(cols[priceIdx] || "0") * 100),
                };
            }).filter((r) => r.variantId && r.sellingPrice > 0);

            setParsedRows(rows);
        };
        reader.readAsText(file);
    };

    const handleSubmit = async () => {
        if (!startsAt || !endsAt) {
            showError("Please set start and end dates");
            return;
        }
        if (parsedRows.length === 0) {
            showError("No valid rows in CSV");
            return;
        }

        try {
            const res = await bulkCreate.mutateAsync({
                startsAt: new Date(startsAt).toISOString(),
                endsAt: new Date(endsAt).toISOString(),
                platforms: [platform],
                notes: notes || undefined,
                rows: parsedRows,
            });
            setResult(res);
            if (res.created.length > 0) {
                showSuccess(`${res.created.length} schedule(s) created`);
            }
            if (res.errors.length > 0) {
                showError(`${res.errors.length} row(s) had errors`);
            }
        } catch (err: any) {
            showError(err.response?.data?.message || err.message);
        }
    };

    const handleClose = () => {
        setCsvFile(null);
        setParsedRows([]);
        setResult(null);
        setStartsAt("");
        setEndsAt("");
        setNotes("");
        setPlatform(0);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                Bulk Upload Price Schedules
            </DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                        <TextField
                            label="Starts At"
                            type="datetime-local"
                            size="small"
                            value={startsAt}
                            onChange={(e) => setStartsAt(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            label="Ends At"
                            type="datetime-local"
                            size="small"
                            value={endsAt}
                            onChange={(e) => setEndsAt(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Platform</InputLabel>
                            <Select
                                value={platform}
                                label="Platform"
                                onChange={(e) => setPlatform(Number(e.target.value))}
                            >
                                <MenuItem value={0}>Website</MenuItem>
                                <MenuItem value={1}>Amazon</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Notes"
                            size="small"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            fullWidth
                        />
                    </Box>

                    <Box>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<UploadIcon />}
                        >
                            {csvFile ? csvFile.name : "Choose CSV File"}
                            <input
                                type="file"
                                accept=".csv"
                                hidden
                                onChange={handleFileChange}
                            />
                        </Button>
                        <Typography variant="caption" color="textSecondary" ml={2}>
                            CSV with columns: variantId, sellingPrice (in ₹)
                        </Typography>
                    </Box>

                    {parsedRows.length > 0 && !result && (
                        <Alert severity="info">
                            {parsedRows.length} row(s) parsed from CSV
                        </Alert>
                    )}

                    {result && (
                        <Box>
                            {result.created.length > 0 && (
                                <Alert severity="success" sx={{ mb: 1 }}>
                                    {result.created.length} schedule(s) created successfully
                                </Alert>
                            )}
                            {result.errors.length > 0 && (
                                <>
                                    <Alert severity="error" sx={{ mb: 1 }}>
                                        {result.errors.length} row(s) failed
                                    </Alert>
                                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200, overflowX: "auto" }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Row</TableCell>
                                                    <TableCell>Variant ID</TableCell>
                                                    <TableCell>Reason</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {result.errors.map((err, i) => (
                                                    <TableRow key={i}>
                                                        <TableCell>{err.row}</TableCell>
                                                        <TableCell>{err.variantId}</TableCell>
                                                        <TableCell>
                                                            <Chip label={err.reason} color="error" size="small" />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={parsedRows.length === 0 || bulkCreate.isLoading}
                >
                    {bulkCreate.isLoading ? "Uploading..." : "Upload"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
